import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { AField, BiField, Btn, PageHead } from "@/admin/AdminLayout";
import { ImageUploader } from "@/admin/ImageUploader";

const EMPTY = {
  make: "", model: "", version: "", year: "", mileage: "", price: "", currency: "EUR", price_visible: true, status: "available", published: false, featured: false,
  sort_order: 0, vehicle_type: "coupe", location: "", country: "", exterior_color: "", interior_color: "", engine: "", power: "", torque: "", transmission: "", fuel: "",
  owners: "", service_history: "", short_description: { fr: "", en: "" }, description: { fr: "", en: "" }, equipment: [], history: { fr: "", en: "" }, condition: { fr: "", en: "" },
  notes: "", seo_title: { fr: "", en: "" }, seo_description: { fr: "", en: "" }, images: [], cover_index: 0, video_url: "", slug: "",
};
const TYPES = ["coupe", "convertible", "suv", "sedan", "hypercar", "classic", "gt", "track"];
const Section = ({ title, children }) => <div className="admin-card p-5"><p className="mono mb-5 text-[0.58rem] text-[#8b95a5]">{title}</p><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div></div>;

export default function VehicleForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [v, setV] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const { data: videos = [] } = useQuery({ queryKey: ["admin-media", "video"], queryFn: async () => (await api.get("/admin/media?kind=video")).data });
  const { data } = useQuery({ queryKey: ["admin-vehicle", id], queryFn: async () => (await api.get(`/admin/vehicles/${id}`)).data, enabled: !!id });
  useEffect(() => { if (data) setV({ ...EMPTY, ...data, year: data.year ?? "", mileage: data.mileage ?? "", price: data.price ?? "", owners: data.owners ?? "" }); }, [data]);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setBi = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const inp = (k, props = {}) => <input className="field" value={v[k] ?? ""} onChange={set(k)} data-testid={`vehicle-${k}`} {...props} />;

  const save = async (publish) => {
    if (!v.make || !v.model) return toast.error("Make and model are required");
    setBusy(true);
    const num = (x) => (x === "" || x == null ? null : Number(x));
    const body = { ...v, year: num(v.year), mileage: num(v.mileage), price: num(v.price), owners: num(v.owners), sort_order: Number(v.sort_order) || 0,
      equipment: Array.isArray(v.equipment) ? v.equipment : String(v.equipment).split("\n").map((s) => s.trim()).filter(Boolean), published: publish ?? v.published, slug: v.slug || null };
    delete body.id; delete body.created_at; delete body.updated_at; delete body.similar;
    try {
      const res = id ? await api.put(`/admin/vehicles/${id}`, body) : await api.post("/admin/vehicles", body);
      toast.success("Vehicle saved");
      ["admin-vehicles", "vehicles", "featured", "filters", "admin-vehicle"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      if (!id) nav(`/admin/vehicles/${res.data.id}`, { replace: true });
      else setV((s) => ({ ...s, slug: res.data.slug }));
      return res.data;
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const preview = async () => {
    const saved = await save();
    if (saved) window.open(`/vehicles/${saved.slug}`, "_blank");
  };
  const equipmentText = Array.isArray(v.equipment) ? v.equipment.join("\n") : v.equipment;

  return (
    <div data-testid="admin-vehicle-form">
      <PageHead title={id ? `${v.make} ${v.model}` : "Add Vehicle"}>
        <Btn variant="ghost" onClick={preview} disabled={busy} data-testid="vehicle-preview">PREVIEW</Btn>
        <Btn variant="ghost" onClick={() => save(false)} disabled={busy} data-testid="vehicle-save-draft">SAVE DRAFT</Btn>
        <Btn onClick={() => save(true)} disabled={busy} data-testid="vehicle-save-publish">SAVE & PUBLISH</Btn>
      </PageHead>
      <div className="space-y-5">
        <Section title="Identity">
          <AField label="Manufacturer *">{inp("make")}</AField>
          <AField label="Model *">{inp("model")}</AField>
          <AField label="Version">{inp("version")}</AField>
          <AField label="Year">{inp("year", { type: "number" })}</AField>
          <AField label="Mileage (km)">{inp("mileage", { type: "number" })}</AField>
          <AField label="Vehicle type"><select className="field" value={v.vehicle_type} onChange={set("vehicle_type")} data-testid="vehicle-vehicle_type">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></AField>
          <AField label="Location (city)">{inp("location")}</AField>
          <AField label="Country">{inp("country")}</AField>
          <AField label="URL slug (auto if empty)">{inp("slug")}</AField>
        </Section>
        <Section title="Price & status">
          <AField label="Price">{inp("price", { type: "number" })}</AField>
          <AField label="Currency"><select className="field" value={v.currency} onChange={set("currency")}>{["EUR", "CHF", "GBP", "USD", "AED"].map((c) => <option key={c}>{c}</option>)}</select></AField>
          <AField label="Status"><select className="field" value={v.status} onChange={set("status")} data-testid="vehicle-status">{["available", "reserved", "sold", "off_market", "coming_soon"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></AField>
          <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!v.price_visible} onChange={set("price_visible")} data-testid="vehicle-price_visible" /> Show price (otherwise "Price on request")</label>
          <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!v.featured} onChange={set("featured")} data-testid="vehicle-featured" /> Feature on homepage</label>
          <AField label="Display order">{inp("sort_order", { type: "number" })}</AField>
        </Section>
        <Section title="Technical">
          <AField label="Engine">{inp("engine")}</AField>
          <AField label="Power">{inp("power")}</AField>
          <AField label="Torque">{inp("torque")}</AField>
          <AField label="Transmission">{inp("transmission")}</AField>
          <AField label="Fuel">{inp("fuel")}</AField>
          <AField label="Exterior colour">{inp("exterior_color")}</AField>
          <AField label="Interior colour">{inp("interior_color")}</AField>
          <AField label="Number of owners">{inp("owners", { type: "number" })}</AField>
          <AField label="Service history">{inp("service_history")}</AField>
        </Section>
        <div className="admin-card space-y-5 p-5">
          <p className="mono text-[0.58rem] text-[#8b95a5]">Content (FR / EN)</p>
          <BiField label="Short description" value={v.short_description} onChange={setBi("short_description")} textarea rows={2} />
          <BiField label="Full description" value={v.description} onChange={setBi("description")} textarea rows={5} />
          <BiField label="History" value={v.history} onChange={setBi("history")} textarea />
          <BiField label="Condition" value={v.condition} onChange={setBi("condition")} textarea />
          <AField label="Equipment (one per line)"><textarea className="field" rows={5} value={equipmentText} onChange={(e) => setV((s) => ({ ...s, equipment: e.target.value.split("\n") }))} data-testid="vehicle-equipment" /></AField>
          <AField label="Internal notes (never public)"><textarea className="field" rows={2} value={v.notes} onChange={set("notes")} data-testid="vehicle-notes" /></AField>
        </div>
        <div className="admin-card p-5">
          <p className="mono mb-5 text-[0.58rem] text-[#8b95a5]">Photos — drag & drop, reorder, cover</p>
          <ImageUploader images={v.images} coverIndex={v.cover_index} onChange={(images, cover_index) => setV((s) => ({ ...s, images, cover_index }))} />
          <AField label="Vehicle video (optional, replaces hero image)" className="mt-5">
            <select className="field" value={v.video_url} onChange={set("video_url")} data-testid="vehicle-video">
              <option value="">— none —</option>
              {videos.map((m) => <option key={m.id} value={m.url}>{m.original_filename || m.url}</option>)}
            </select>
          </AField>
        </div>
        <div className="admin-card space-y-5 p-5">
          <p className="mono text-[0.58rem] text-[#8b95a5]">SEO</p>
          <BiField label="SEO title" value={v.seo_title} onChange={setBi("seo_title")} />
          <BiField label="SEO description" value={v.seo_description} onChange={setBi("seo_description")} textarea rows={2} />
        </div>
      </div>
    </div>
  );
}
